// frontend/src/mobile/MobileInvoices.jsx
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera, FilePlus2 } from "lucide-react";
import { toast } from "sonner";
import { API, apiFetch } from "@/utils/api";
import { formatCurrency } from "@/utils/format";
import OCRPreviewDialog from "@/components/OCRPreviewDialog";
import InvoiceForm from "@/components/InvoiceEmittedForm";
import InvoiceCard from "./InvoiceCard";
import MobileInvoiceSheet from "./MobileInvoiceSheet";

const PAGE = 20;

export default function MobileInvoices({ initialTab }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const kind = initialTab; // "emitted" | "received" — fijado por la ruta
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);      // emitidas: {pending_amount_eur, pending_count, overdue_count}
  const [recvPending, setRecvPending] = useState(null); // recibidas: nº pendientes de contabilizar
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [sheetInv, setSheetInv] = useState(null);
  const loadSeq = useRef(0);

  // Barra de acciones estrella (Task 7, D6/D7): foto-OCR + crear factura.
  const cameraRef = useRef(null);
  const [ocrQueue, setOcrQueue] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [clients, setClients] = useState([]);

  const base = kind === "emitted" ? "invoices/emitted" : "invoices/received";

  const loadAll = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setError(false);
    try {
      const reqs = [apiFetch(`${API}/${base}?skip=0&limit=${PAGE}`)];
      if (kind === "emitted") reqs.push(apiFetch(`${API}/invoices/emitted/summary`));
      else reqs.push(apiFetch(`${API}/invoices/received?status=pending&limit=1`));
      const [listRes, extraRes] = await Promise.all(reqs);
      if (seq !== loadSeq.current) return;
      if (!listRes.ok) { setError(true); return; }
      const data = await listRes.json();
      if (seq !== loadSeq.current) return;
      setItems(data.invoices ?? []);
      setTotal(data.total ?? 0);
      if (extraRes?.ok) {
        const extra = await extraRes.json();
        if (seq !== loadSeq.current) return;
        if (kind === "emitted") setSummary(extra);
        else setRecvPending(extra.total ?? 0);
      }
    } catch (e) {
      console.error("MobileInvoices load", e);
      if (seq === loadSeq.current) setError(true);
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [base, kind]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Deep-link ?open=<invoice_id> (desde la vencida de Inicio).
  // Consumido UNA vez por montaje: si el usuario cierra la hoja, un "Cargar
  // más" posterior (items cambia) no debe reabrirla.
  const openConsumedRef = useRef(false);
  useEffect(() => {
    if (openConsumedRef.current) return;
    const openId = searchParams.get("open");
    if (openId && items.length) {
      const hit = items.find((i) => i.invoice_id === openId);
      if (hit) { openConsumedRef.current = true; setSheetInv(hit); }
    }
  }, [searchParams, items]);

  const loadMore = async () => {
    if (loadingMore || items.length >= total) return;
    const seq = loadSeq.current; // una recarga (loadAll) invalida esta página en vuelo
    setLoadingMore(true);
    try {
      const r = await apiFetch(`${API}/${base}?skip=${items.length}&limit=${PAGE}`);
      if (r.ok && seq === loadSeq.current) {
        const data = await r.json();
        if (seq === loadSeq.current) {
          setItems((prev) => [...prev, ...(data.invoices ?? [])]);
          setTotal(data.total ?? total);
        }
      }
    } finally {
      setLoadingMore(false);
    }
  };

  // Clientes para el selector de "Crear factura" (siempre factura EMITIDA,
  // disponible desde ambas pestañas) — calcado de InvoicesEmittedDesktop.fetchClients:
  // GET /crm/clients devuelve el array de clientes tal cual (sin envolver).
  const fetchClients = useCallback(async () => {
    try {
      const r = await apiFetch(`${API}/crm/clients`);
      if (r.ok) setClients(await r.json());
    } catch (e) {
      console.error("MobileInvoices fetchClients", e);
    }
  }, []);
  useEffect(() => { fetchClients(); }, [fetchClients]);

  // "Fotografiar gasto" (D6): sube el/los archivo(s) capturados. Calcado de
  // InvoicesReceived.handleFileUpload — mismo campo multipart "file", mismo
  // criterio de cola OCR y mismos mensajes de error.
  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const r = await apiFetch(`${API}/documents/upload`, { method: "POST", body: fd });
        if (r.ok) {
          const doc = await r.json();
          if (doc.ocr_status === "completed") setOcrQueue((q) => [...q, doc]);
          else toast.info(`${file.name}: subido, procesando OCR...`);
        } else {
          const err = await r.json().catch(() => ({}));
          toast.error(err.detail || `Error al subir ${file.name}`, { duration: 8000 });
        }
      } catch {
        toast.error(`Error al subir ${file.name}`);
      }
    }
  };

  // Crear el gasto (factura recibida) desde el documento OCR de cabeza de cola.
  // Calcado de InvoicesReceived.handleCreateFromOCR: create-invoice se llama
  // SIN body — la categoría de proveedor (si se eligió) se guarda aparte con
  // un PUT a /supplier-categories, igual que en escritorio.
  const handleCreateFromOCR = async (documentId, supplierCategory) => {
    try {
      const r = await apiFetch(`${API}/documents/${documentId}/create-invoice`, { method: "POST" });
      if (!r.ok) { toast.error("No se pudo crear el gasto"); return; }
      const invoice = await r.json();
      if (supplierCategory && invoice.supplier_name) {
        apiFetch(`${API}/supplier-categories`, {
          method: "PUT",
          body: JSON.stringify({ supplier_name: invoice.supplier_name, category: supplierCategory }),
        }).catch(() => { /* silencioso, como en escritorio */ });
      }
      if (invoice.is_duplicate) toast.warning("Posible factura duplicada detectada");
      else toast.success("Gasto creado desde OCR — «Pendiente» hasta que se contabilice (escritorio)");
      setOcrQueue((q) => q.slice(1));
      if (kind === "received") loadAll();
      else navigate("/facturas/recibidas");
    } catch {
      toast.error("No se pudo crear el gasto");
    }
  };

  // "Crear factura" (D7): siempre da de alta una factura EMITIDA. Calcado de
  // InvoicesEmittedDesktop.handleSaveInvoice (rama de alta nueva) — el form ya
  // entrega el payload montado, se postea tal cual sin transformación previa.
  const handleSaveNew = async (payload) => {
    try {
      const r = await apiFetch(`${API}/invoices/emitted`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (r.ok) {
        toast.success("Factura creada");
        setNewOpen(false);
        if (kind === "emitted") loadAll();
        else navigate("/facturas/emitidas");
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Error al guardar factura", { duration: 9000 });
      }
    } catch {
      toast.error("Error al guardar factura");
    }
  };

  const seg = (active) =>
    `flex-1 min-h-[44px] rounded-xl text-[13px] font-semibold transition-colors ${
      active ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
    }`;

  return (
    <div data-testid="mobile-invoices" className="pb-[132px]">
      <h1 className="font-heading text-2xl font-bold text-slate-900">Facturas</h1>

      <div className="mt-3.5 flex gap-1 rounded-2xl bg-slate-200/60 p-[3px]">
        <button data-testid="minv-seg-emitted" className={seg(kind === "emitted")}
          onClick={() => kind !== "emitted" && navigate("/facturas/emitidas")}>
          Emitidas{kind === "emitted" && total ? ` (${total})` : ""}
        </button>
        <button data-testid="minv-seg-received" className={seg(kind === "received")}
          onClick={() => kind !== "received" && navigate("/facturas/recibidas")}>
          Recibidas{kind === "received" && total ? ` (${total})` : ""}
        </button>
      </div>

      {kind === "emitted" && summary && (
        <p data-testid="minv-summary" className="mt-2.5 px-0.5 text-[11.5px] text-slate-500">
          Pendiente de cobro <span className="font-mono font-semibold text-slate-900">{formatCurrency(summary.pending_amount_eur)}</span>
          {summary.overdue_count > 0 && (
            <> · <span className="font-semibold text-rose-600">{summary.overdue_count} vencida{summary.overdue_count > 1 ? "s" : ""}</span></>
          )}{" "}· los borradores no cuentan
        </p>
      )}
      {kind === "received" && recvPending != null && (
        <p data-testid="minv-summary" className="mt-2.5 px-0.5 text-[11.5px] text-slate-500">
          <span className="font-mono font-semibold text-amber-700">{recvPending}</span>{" "}
          pendiente{recvPending === 1 ? "" : "s"} de contabilizar · el pago nace de la conciliación
        </p>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><div className="spinner" /></div>
      ) : error ? (
        <div data-testid="minv-error" className="mt-10 bg-white border border-slate-200 rounded-2xl p-5 text-center">
          <p className="text-sm text-slate-700">No se pudieron cargar las facturas. Comprueba la conexión.</p>
          <button onClick={loadAll} className="mt-3 min-h-[44px] px-5 rounded-2xl bg-slate-900 text-white text-sm font-semibold">
            Reintentar
          </button>
        </div>
      ) : (
        <>
          <div className="mt-2.5 flex flex-col gap-2">
            {items.map((inv) => (
              <InvoiceCard key={inv.invoice_id} inv={inv} kind={kind} onTap={setSheetInv} />
            ))}
            {items.length === 0 && (
              <p className="py-14 text-center text-sm text-slate-500">No hay facturas todavía.</p>
            )}
          </div>
          {items.length < total && (
            <button onClick={loadMore} disabled={loadingMore}
              className="mt-3 w-full min-h-[44px] rounded-2xl border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50">
              {loadingMore ? "Cargando…" : "Cargar más"}
            </button>
          )}
        </>
      )}
      <MobileInvoiceSheet
        invoice={sheetInv}
        kind={kind}
        onClose={() => {
          setSheetInv(null);
          if (searchParams.get("open")) {
            setSearchParams((p) => { p.delete("open"); return p; }, { replace: true });
          }
        }}
      />

      {/* Input de cámara: sin multiple (capture+multiple es errático en Android); los specs e2e lo localizan por data-testid="camera-file-input". */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        data-testid="camera-file-input"
        onChange={(e) => { handleFiles(Array.from(e.target.files ?? [])); e.target.value = ""; }}
      />

      {/* Barra de acciones estrella (D6/D7) — fija encima de la nav (64 px) */}
      <div className="fixed inset-x-0 bottom-16 z-30 px-4 pb-3 pt-6 bg-gradient-to-t from-slate-50 via-slate-50/90 to-transparent lg:hidden">
        <div className="flex gap-2">
          <button
            data-testid="camera-upload-btn"
            onClick={() => cameraRef.current?.click()}
            className="flex-[1.25] min-h-[48px] flex items-center justify-center gap-2 rounded-2xl bg-blue-600 text-white text-[13.5px] font-semibold shadow-lg shadow-blue-600/30"
          >
            <Camera className="w-[18px] h-[18px]" strokeWidth={1.5} /> Fotografiar gasto
          </button>
          <button
            data-testid="minv-new-invoice"
            onClick={() => setNewOpen(true)}
            className="flex-1 min-h-[48px] flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white text-[13.5px] font-semibold text-slate-900 shadow"
          >
            <FilePlus2 className="w-4 h-4" strokeWidth={1.5} /> Crear factura
          </button>
        </div>
      </div>

      {/* Cola de revisión OCR — el wrapper Dialog calca el mount de escritorio
          (InvoicesReceived.jsx): SIN onOpenChange, solo se cierra por los
          botones internos de OCRPreviewDialog (Cancelar/Crear) para no perder
          el documento si el usuario toca fuera o pulsa Escape. */}
      {ocrQueue.length > 0 && (
        <Dialog open>
          <OCRPreviewDialog
            document={ocrQueue[0]}
            queueRemaining={ocrQueue.length}
            onCreateInvoice={handleCreateFromOCR}
            onClose={() => setOcrQueue((q) => q.slice(1))}
          />
        </Dialog>
      )}

      {/* Nueva factura emitida (D7) */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nueva Factura Emitida</DialogTitle></DialogHeader>
          <InvoiceForm
            invoice={null}
            onSave={handleSaveNew}
            onCancel={() => setNewOpen(false)}
            clients={clients}
            onClientCreated={fetchClients}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
